import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiArrowDown,
  FiArrowLeft,
  FiArrowRight,
  FiArrowUp,
  FiBookOpen,
  FiEdit2,
  FiLock,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUnlock,
  FiX,
} from 'react-icons/fi';
import { createCurriculumService } from './curriculumService';
import { LAYOUT_TYPES } from './curriculumLibrariesService';
import CurriculumMiniInputModal from './CurriculumMiniInputModal';
import './Curriculum.css';

function TextbookEditorModal({ modalState, onClose, onSubmit, submitting }) {
  const isEdit = modalState.mode === 'edit';

  return (
    <div className="apcModal__overlay" role="dialog" aria-modal="true">
      <div className="apcModal__card">
        <div className="apcModal__header">
          <h3>{isEdit ? 'Edit Textbook' : 'Add Textbook'}</h3>
          <button type="button" className="apcIconBtn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="apcModal__body">
          <label className="apcField">
            <span>Textbook Title</span>
            <input
              value={modalState.title}
              onChange={(e) => modalState.setTitle(e.target.value)}
              placeholder="Textbook title"
            />
          </label>

          <label className="apcField">
            <span>Cover Source</span>
            <select value={modalState.coverMode} onChange={(e) => modalState.setCoverMode(e.target.value)}>
              {!isEdit && <option value="none">No Cover</option>}
              {!isEdit && <option value="file">Upload Cover Image</option>}
              {!isEdit && <option value="url">Paste Cover URL</option>}

              {isEdit && <option value="keep">Keep Existing Cover</option>}
              {isEdit && <option value="file">Replace with Uploaded Image</option>}
              {isEdit && <option value="url">Replace with URL</option>}
              {isEdit && <option value="remove">Remove Cover</option>}
            </select>
          </label>

          {modalState.coverMode === 'url' && (
            <label className="apcField">
              <span>Cover URL</span>
              <input
                value={modalState.coverUrl}
                onChange={(e) => modalState.setCoverUrl(e.target.value)}
                placeholder="https://..."
              />
            </label>
          )}

          {modalState.coverMode === 'file' && (
            <label className="apcField">
              <span>Cover Image File</span>
              <input type="file" accept="image/*" onChange={(e) => modalState.setCoverFile(e.target.files?.[0] || null)} />
            </label>
          )}
        </div>

        <div className="apcModal__footer">
          <button type="button" className="apcBtn apcBtn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="apcBtn apcBtn--primary" onClick={onSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Renders a single curriculum library. `category` layout shows one horizontal
// carousel per category with category management; `simple` layout shows a flat
// grid with textbooks added directly to the library's implicit bucket category.
export default function CurriculumLibraryView({ library, canDelete = true, onOpenTextbook }) {
  const service = useMemo(() => createCurriculumService(library), [library]);
  const isCategoryLayout = library.layoutType === LAYOUT_TYPES.CATEGORY;

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [textbookModal, setTextbookModal] = useState(null);
  const [categoryModal, setCategoryModal] = useState(null);
  const [scrollState, setScrollState] = useState({});

  const scrollerRefs = useRef({});

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await service.fetchLibrary();
      setCategories(data);
    } catch (error) {
      alert(error.message || 'Failed to load curriculum library.');
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const categoriesToRender = useMemo(() => {
    if (!normalizedSearch) return categories;
    return categories
      .map((category) => ({
        ...category,
        textbooks: (category.textbooks || []).filter((book) =>
          String(book.title || '').toLowerCase().includes(normalizedSearch)
        ),
      }))
      .filter((category) => category.textbooks.length > 0);
  }, [categories, normalizedSearch]);

  // For simple libraries every textbook lives in the single bucket category.
  const simpleTextbooks = useMemo(() => {
    if (isCategoryLayout) return [];
    const all = categories.flatMap((category) =>
      (category.textbooks || []).map((book) => ({ ...book, categoryId: category.id }))
    );
    if (!normalizedSearch) return all;
    return all.filter((book) => String(book.title || '').toLowerCase().includes(normalizedSearch));
  }, [categories, isCategoryLayout, normalizedSearch]);

  async function ensureSimpleCategoryId() {
    const existing = categories[0]?.id;
    if (existing) return existing;
    return service.ensureDefaultCategory();
  }

  // ---------- Category management (category layout only) ----------
  const openAddCategoryModal = () => {
    setCategoryModal({ mode: 'add', title: 'Add Category', label: 'Category Name', value: '', submitLabel: 'Create', categoryId: '' });
  };

  const openRenameCategoryModal = (category) => {
    setCategoryModal({ mode: 'rename', title: 'Rename Category', label: 'Category Name', value: category.name || '', submitLabel: 'Save', categoryId: category.id });
  };

  const closeCategoryModal = () => setCategoryModal(null);

  const submitCategoryModal = async () => {
    if (!categoryModal) return;
    const name = categoryModal.value?.trim();
    if (!name) return;

    try {
      setSubmitting(true);
      if (categoryModal.mode === 'add') {
        await service.createCategory({ name, orderIndex: categories.length });
      } else {
        await service.renameCategory({ categoryId: categoryModal.categoryId, name });
      }
      closeCategoryModal();
      await loadLibrary();
    } catch (error) {
      alert(error.message || 'Failed to save category.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Delete category "${category.name}" and all textbooks/resources inside it?`)) return;
    try {
      setSubmitting(true);
      await service.deleteCategory({ categoryId: category.id });
      await loadLibrary();
    } catch (error) {
      alert(error.message || 'Failed to delete category.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoveCategory = async (categoryId, direction) => {
    const index = categories.findIndex((category) => category.id === categoryId);
    if (index < 0) return;
    try {
      setSubmitting(true);
      await service.reorderCategories({ categories, fromIndex: index, direction });
      await loadLibrary();
    } catch (error) {
      alert(error.message || 'Failed to reorder category.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Textbook management ----------
  const openAddTextbookModal = (categoryId) => {
    setTextbookModal({ mode: 'add', categoryId, textbook: null, title: '', coverMode: 'none', coverUrl: '', coverFile: null });
  };

  const openEditTextbookModal = (categoryId, textbook) => {
    setTextbookModal({ mode: 'edit', categoryId, textbook, title: textbook.title || '', coverMode: 'keep', coverUrl: textbook.coverImageUrl || '', coverFile: null });
  };

  const closeTextbookModal = () => setTextbookModal(null);

  const submitTextbookModal = async () => {
    if (!textbookModal) return;

    const title = textbookModal.title?.trim();
    if (!title) {
      alert('Please enter a textbook title.');
      return;
    }
    if (textbookModal.coverMode === 'url' && !textbookModal.coverUrl?.trim()) {
      alert('Please enter a cover URL.');
      return;
    }
    if (textbookModal.coverMode === 'file' && !textbookModal.coverFile) {
      alert('Please choose a cover image file.');
      return;
    }

    try {
      setSubmitting(true);

      if (textbookModal.mode === 'add') {
        const categoryId = isCategoryLayout ? textbookModal.categoryId : await ensureSimpleCategoryId();
        const targetCategory = categories.find((category) => category.id === categoryId);
        const orderIndex = targetCategory?.textbooks?.length || (isCategoryLayout ? 0 : simpleTextbooks.length);

        await service.createTextbook({
          categoryId,
          title,
          coverMode: textbookModal.coverMode,
          coverFile: textbookModal.coverFile,
          coverUrl: textbookModal.coverUrl,
          orderIndex,
        });
      } else {
        await service.updateTextbook({
          categoryId: textbookModal.categoryId,
          textbookId: textbookModal.textbook.id,
          title,
          coverUpdate: { mode: textbookModal.coverMode, file: textbookModal.coverFile, url: textbookModal.coverUrl },
        });
      }

      closeTextbookModal();
      await loadLibrary();
    } catch (error) {
      alert(error.message || 'Failed to save textbook.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTextbook = async (categoryId, textbook) => {
    if (!window.confirm(`Delete textbook "${textbook.title}" and all resources?`)) return;
    try {
      setSubmitting(true);
      await service.deleteTextbook({ categoryId, textbookId: textbook.id });
      await loadLibrary();
    } catch (error) {
      alert(error.message || 'Failed to delete textbook.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoveTextbook = async (categoryId, textbookId, direction) => {
    const fullCategory = categories.find((category) => category.id === categoryId);
    const allTextbooks = fullCategory?.textbooks || [];
    const fromIndex = allTextbooks.findIndex((book) => book.id === textbookId);
    if (fromIndex < 0) return;
    try {
      setSubmitting(true);
      await service.reorderTextbooks({ categoryId, textbooks: allTextbooks, fromIndex, direction });
      await loadLibrary();
    } catch (error) {
      alert(error.message || 'Failed to move textbook.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Carousel scroll affordances (category layout) ----------
  const scrollRow = (categoryId, delta) => {
    const node = scrollerRefs.current[categoryId];
    if (!node) return;
    node.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const handleScroll = useCallback((categoryId, node) => {
    if (!node) return;
    const { scrollLeft, scrollWidth, clientWidth } = node;
    setScrollState((prev) => {
      const showLeft = scrollLeft > 0;
      const showRight = scrollLeft < scrollWidth - clientWidth - 2;
      if (prev[categoryId]?.showLeft === showLeft && prev[categoryId]?.showRight === showRight) return prev;
      return { ...prev, [categoryId]: { showLeft, showRight } };
    });
  }, []);

  useEffect(() => {
    const checkAll = () => {
      Object.keys(scrollerRefs.current).forEach((id) => handleScroll(id, scrollerRefs.current[id]));
    };
    checkAll();
    window.addEventListener('resize', checkAll);
    return () => window.removeEventListener('resize', checkAll);
  }, [categoriesToRender, handleScroll]);

  const renderBookCard = (categoryId, textbook) => (
    <article key={textbook.id} className="apcl__bookCard">
      <button type="button" className="apcl__coverBtn" onClick={() => onOpenTextbook(textbook.id)}>
        {textbook.coverImageUrl ? (
          <img src={textbook.coverImageUrl} alt={textbook.title} className="apcl__cover" />
        ) : (
          <div className="apcl__coverPlaceholder">
            <FiBookOpen />
          </div>
        )}
      </button>

      <button type="button" className="apcl__bookTitle" onClick={() => onOpenTextbook(textbook.id)} title={textbook.title}>
        {textbook.title}
      </button>

      {isEditMode && (
        <div className="apcl__bookActions">
          <button type="button" className="apcIconBtn" onClick={() => handleMoveTextbook(categoryId, textbook.id, 'left')}>
            <FiArrowLeft />
          </button>
          <button type="button" className="apcIconBtn" onClick={() => handleMoveTextbook(categoryId, textbook.id, 'right')}>
            <FiArrowRight />
          </button>
          <button type="button" className="apcIconBtn" onClick={() => openEditTextbookModal(categoryId, textbook)}>
            <FiEdit2 />
          </button>
          <button type="button" className="apcIconBtn apcIconBtn--danger" onClick={() => handleDeleteTextbook(categoryId, textbook)}>
            <FiTrash2 />
          </button>
        </div>
      )}
    </article>
  );

  return (
    <div className={`apcl ${canDelete ? '' : 'apcl--noDelete'}`}>
      <div className="apcl__header">
        <div className="apcl__titleWrap">
          <h1>{library.name}</h1>
          <p>{library.description || `${library.name} Library`}</p>
        </div>

        <div className="apcl__headerActions">
          <div className="apcl__search">
            <FiSearch />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search textbooks" />
          </div>

          <button
            type="button"
            className="apcIconBtn apcIconBtn--lock"
            onClick={() => setIsEditMode((prev) => !prev)}
            title={isEditMode ? 'Lock Edit Mode' : 'Unlock Edit Mode'}
          >
            {isEditMode ? <FiUnlock /> : <FiLock />}
          </button>
        </div>
      </div>

      {isEditMode && (
        <div className="apcl__toolbar">
          {isCategoryLayout ? (
            <button type="button" className="apcBtn apcBtn--primary" onClick={openAddCategoryModal} disabled={submitting}>
              <FiPlus /> Add Category
            </button>
          ) : (
            <button type="button" className="apcBtn apcBtn--primary" onClick={() => openAddTextbookModal(categories[0]?.id || '')} disabled={submitting}>
              <FiPlus /> Add Textbook
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="apcLoading">Loading curriculum...</div>
      ) : isCategoryLayout ? (
        <div className="apcl__rows">
          {categoriesToRender.map((category) => (
            <section key={category.id} className="apcl__row">
              <div className="apcl__rowHeader">
                <h2>{category.name}</h2>

                {isEditMode && (
                  <div className="apcl__rowActions">
                    <button type="button" className="apcIconBtn" onClick={() => handleMoveCategory(category.id, 'up')}>
                      <FiArrowUp />
                    </button>
                    <button type="button" className="apcIconBtn" onClick={() => handleMoveCategory(category.id, 'down')}>
                      <FiArrowDown />
                    </button>
                    <button type="button" className="apcIconBtn" onClick={() => openRenameCategoryModal(category)}>
                      <FiEdit2 />
                    </button>
                    <button type="button" className="apcIconBtn apcIconBtn--danger" onClick={() => handleDeleteCategory(category)}>
                      <FiTrash2 />
                    </button>
                    <button type="button" className="apcIconBtn" onClick={() => openAddTextbookModal(category.id)}>
                      <FiPlus />
                    </button>
                  </div>
                )}
              </div>

              <div className="apcl__carouselWrap">
                {scrollState[category.id]?.showLeft && (
                  <button type="button" className="apcl__scrollBtn apcl__scrollBtn--left" onClick={() => scrollRow(category.id, -320)}>
                    <FiArrowLeft />
                  </button>
                )}

                <div
                  className="apcl__carousel"
                  onScroll={(e) => handleScroll(category.id, e.target)}
                  ref={(node) => {
                    scrollerRefs.current[category.id] = node;
                  }}
                >
                  {(category.textbooks || []).map((textbook) => renderBookCard(category.id, textbook))}

                  {isEditMode && (
                    <button type="button" className="apcl__addBookCard" onClick={() => openAddTextbookModal(category.id)}>
                      <FiPlus />
                      <span>Add Textbook</span>
                    </button>
                  )}
                </div>

                {scrollState[category.id]?.showRight && (
                  <button type="button" className="apcl__scrollBtn apcl__scrollBtn--right" onClick={() => scrollRow(category.id, 320)}>
                    <FiArrowRight />
                  </button>
                )}
              </div>

              {category.textbooks?.length === 0 && (
                <div className="apcl__emptyRow">No textbooks in this category yet.</div>
              )}
            </section>
          ))}

          {categoriesToRender.length === 0 && (
            <div className="apcl__emptyAll">
              {normalizedSearch
                ? 'No textbooks match your search.'
                : 'No categories yet. Unlock edit mode to create the first category.'}
            </div>
          )}
        </div>
      ) : (
        <div className="satcl__grid">
          {simpleTextbooks.map((textbook) => renderBookCard(textbook.categoryId, textbook))}

          {isEditMode && (
            <button type="button" className="apcl__addBookCard" onClick={() => openAddTextbookModal(categories[0]?.id || '')}>
              <FiPlus />
              <span>Add Textbook</span>
            </button>
          )}

          {!isEditMode && simpleTextbooks.length === 0 && (
            <div className="apcl__emptyAll">
              {normalizedSearch ? 'No textbooks match your search.' : 'No textbooks yet.'}
            </div>
          )}
        </div>
      )}

      {textbookModal && (
        <TextbookEditorModal
          modalState={{
            ...textbookModal,
            setTitle: (value) => setTextbookModal((prev) => ({ ...prev, title: value })),
            setCoverMode: (value) => setTextbookModal((prev) => ({ ...prev, coverMode: value })),
            setCoverUrl: (value) => setTextbookModal((prev) => ({ ...prev, coverUrl: value })),
            setCoverFile: (value) => setTextbookModal((prev) => ({ ...prev, coverFile: value })),
          }}
          onClose={closeTextbookModal}
          onSubmit={submitTextbookModal}
          submitting={submitting}
        />
      )}

      {categoryModal && (
        <CurriculumMiniInputModal
          title={categoryModal.title}
          label={categoryModal.label}
          value={categoryModal.value}
          onChange={(value) => setCategoryModal((prev) => ({ ...prev, value }))}
          onCancel={closeCategoryModal}
          onSubmit={submitCategoryModal}
          submitting={submitting}
          submitLabel={categoryModal.submitLabel}
        />
      )}
    </div>
  );
}
